
import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { CandidateAssessmentSummary } from '../../types/job.types';
import { SpiderChart } from './SpiderChart';
import { PsychometricResults } from './PsychometricResults';
import {
  getSkillsData,
  getAssessmentScores,
} from '../../hooks/useAssessmentData';
import { Tag } from '../ui/atoms/Tag';
import { getScoreIntent } from '../../utils/tag.utils';
import { SuspiciousActivityBadge } from '../ui/SuspiciousActivityBadge';
import { useCandidateQuestionsAnswers } from '../../hooks/useJobOffers';
import {
  hasTechnicalCheating,
  hasSoftCheating,
  getTooltipsWithSuspicion,
} from '../../utils/cheatingDetection.utils';
import { getAssessmentContentLocale } from '@/utils/locale';
import { getFixedTForContentLocale } from '@/utils/locale';

interface CandidateAssessmentProps {
  assessment: CandidateAssessmentSummary | null;
  isLoadingAssessmentSummary: boolean;
}

export const CandidateAssessment: React.FC<CandidateAssessmentProps> = ({ 
  assessment,
  isLoadingAssessmentSummary
}) => {
  const { t } = useTranslation('candidates');
  const assessmentContentLocale = getAssessmentContentLocale(assessment);
  const tInferred = useMemo(
    () => getFixedTForContentLocale(assessmentContentLocale),
    [assessmentContentLocale],
  );

  // State for collapsible sections
  const [isFeedbackExpanded, setIsFeedbackExpanded] = useState(false);
  const [isFinalRecommendationExpanded, setIsFinalRecommendationExpanded] = useState(false);

  // Use assessment data extraction
  const scores = getAssessmentScores(assessment);
  const { fitScore, techScore, softScore } = scores;

  // Fetch questions to determine which section has suspicious activity
  const { data: questionsData } = useCandidateQuestionsAnswers(assessment?.candidate_id || '', {
    enabled: !!assessment?.candidate_id
  });

  const hasTechnicalCheatingFlag = React.useMemo(() => {
    return hasTechnicalCheating(questionsData, assessment);
  }, [questionsData, assessment]);

  const hasSoftCheatingFlag = React.useMemo(() => {
    return hasSoftCheating(questionsData, assessment);
  }, [questionsData, assessment]);
  
  return (
    <div className="space-y-6">
      <h5 className="text-dark-invirtus border-b border-gray-200 pb-2 font-heading">{t('meta_cv.assessment.title')}</h5>
      
      {isLoadingAssessmentSummary ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple"></div>
        </div>
      ) : assessment?.is_processing ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <div className="flex justify-center mb-4">
            <svg className="animate-spin h-8 w-8 text-yellow-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          </div>
          <h6 className="text-yellow-800 mb-2 font-heading">{t('meta_cv.assessment.processing_title')}</h6>
          <p className="text-yellow-700 font-body">
            {t('meta_cv.assessment.processing_desc')}
          </p>
        </div>
      ) : !assessment ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-medium text-red-800 mb-2 font-heading">{t('meta_cv.assessment.unavailable_title')}</h3>
          <p className="text-red-700 font-body">
            {t('meta_cv.assessment.unavailable_desc')}
          </p>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Feedback Sections at the Top */}
          {(assessment.final_recommendation || assessment.fit_assessment?.executive_summary) && (
            <div className="space-y-4">
              {/* Overall Recommendation - Full Width, white background */}
              <div className="bg-white p-4 rounded-lg border border-gray-200">
                <div
                  className="flex items-center justify-between cursor-pointer"
                  onClick={() => setIsFinalRecommendationExpanded(!isFinalRecommendationExpanded)}
                >
                  <div className="flex items-center gap-3 flex-wrap">
                    <h6 className="text-dark-invirtus font-heading">{t('meta_cv.assessment.overall_recommendation')}</h6>
                    {fitScore !== null && fitScore !== undefined && (
                      <Tag intent={getScoreIntent(fitScore)} variant="subtle" size="lg">
                        {fitScore}%
                      </Tag>
                    )}
                    {assessment?.has_cheating_flag && (
                      <SuspiciousActivityBadge size="lg" />
                    )}
                  </div>
                  <svg
                    className={`w-5 h-5 text-dark-invirtus transform transition-transform ${isFinalRecommendationExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {isFinalRecommendationExpanded && (
                  <div className="text-dark-invirtus/80 whitespace-pre-line mt-3 font-body space-y-3">
                    {assessment.final_recommendation && (
                      <div className="text-sm">{assessment.final_recommendation}</div>
                    )}
                    {assessment.fit_assessment?.executive_summary && (
                      <div>
                        <strong className='font-heading text-dark-invirtus/90'>{t('meta_cv.assessment.executive_summary')}</strong><br/>
                        <span className="text-sm">{assessment.fit_assessment.executive_summary}</span>
                      </div>
                    )}
                    {(assessment.fit_assessment?.key_strengths_aligning_with_role ?? []).length > 0 && (
                      <div>
                        <strong className='font-heading text-dark-invirtus/90'>{t('meta_cv.assessment.key_strengths')}</strong>
                        <ul className="list-disc ml-6 text-sm">
                          {(assessment.fit_assessment?.key_strengths_aligning_with_role ?? []).map((s: string, i: number) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(assessment.fit_assessment?.areas_of_concern_and_development ?? []).length > 0 && (
                      <div>
                        <strong className='font-heading text-dark-invirtus/90'>{t('meta_cv.assessment.areas_of_concern')}</strong>
                        <ul className="list-disc ml-6 text-sm">
                          {(assessment.fit_assessment?.areas_of_concern_and_development ?? []).map((s: string, i: number) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(assessment.fit_assessment?.specific_recommendations_for_hiring ?? []).length > 0 && (
                      <div>
                        <strong className='font-heading text-dark-invirtus/90'>{t('meta_cv.assessment.hiring_recommendations')}</strong>
                        <ul className="list-disc ml-6 text-sm">
                          {(assessment.fit_assessment?.specific_recommendations_for_hiring ?? []).map((s: string, i: number) => (
                            <li key={i}>{s}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {assessment.fit_assessment?.risk_assessment_and_mitigation_stratgies && (
                      <div>
                        <strong className='font-heading text-dark-invirtus/90'>{t('meta_cv.assessment.risk_assessment')}</strong><br/>
                        <span className="text-sm">{assessment.fit_assessment.risk_assessment_and_mitigation_stratgies}</span>
                      </div>
                    )}
                    {assessment.fit_assessment?.expected_onboarding_and_rampup_timeline && (
                      <div>
                        <strong className='text-dark-invirtus/90'>{t('meta_cv.assessment.onboarding_timeline')}</strong><br/>
                        <span className="text-sm">{assessment.fit_assessment.expected_onboarding_and_rampup_timeline}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Language Assessment Section (if present) */}
              {assessment.language_assessment && assessment.language_assessment.cefr_level != null && (
                <div className="bg-white p-4 rounded-lg border border-gray-200">
                  <h6 className="mb-3">
                    {t('meta_cv.assessment.language_assessment')}
                    {assessment.language_assessment.spoken_language && (
                      <Tag intent="info" variant="subtle" size="lg" className="ml-2">
                        {assessment.language_assessment.spoken_language}
                      </Tag>
                    )}
                    <Tag intent="success" variant="subtle" size="lg" className="ml-2">
                      CEFR {assessment.language_assessment.cefr_level}
                    </Tag>
                  </h6>
                  <div className='font-body text-sm text-dark-invirtus/80'>{assessment.language_assessment.language_feedback}</div>
                </div>
              )}
            </div>
          )}

          {/* Psychometric / MBTI Results */}
          {assessment?.psychometric_results && (
            <PsychometricResults
              psychometricResults={assessment.psychometric_results}
              contentLocale={assessmentContentLocale}
            />
          )}

          {/* Technical and Soft Skills Feedback in 2 Columns */}
          {(() => {
            const techBreakdown = assessment.technical_assessment?.skill_breakdown;
            const softBreakdown = assessment.soft_skills_assessment?.skill_breakdown;
            const hasV3Breakdown = techBreakdown && Object.keys(techBreakdown).length > 0;
            const hasV2Strengths = assessment.technical_assessment?.strengths || assessment.soft_skills_assessment?.strengths;
            const hasV1Recommendation = assessment.technical_assessment?.recommendation || assessment.soft_skills_assessment?.recommendation;

            if (hasV2Strengths) {
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {assessment.technical_assessment?.strengths && (
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsFeedbackExpanded(!isFeedbackExpanded)}>
                        <div className="flex items-center gap-3 flex-wrap">
                          <h6>{t('meta_cv.assessment.technical_feedback')}</h6>
                          {techScore !== null && techScore !== undefined && (
                            <Tag intent={getScoreIntent(techScore)} variant="subtle" size="lg">
                              {techScore}%
                            </Tag>
                          )}
                          {hasTechnicalCheatingFlag && <SuspiciousActivityBadge  size="lg"/>}
                        </div>
                        <svg className={`w-5 h-5 text-dark-invirtus transform transition-transform ${isFeedbackExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      {isFeedbackExpanded && (
                        <div className="mt-3 space-y-3">
                          {(assessment.technical_assessment?.strengths ?? []).length > 0 && (
                            <div>
                              <strong>{t('meta_cv.assessment.strengths')}</strong>
                              <ul className="list-disc ml-6 text-dark-invirtus/80 text-sm">
                                {(assessment.technical_assessment?.strengths ?? []).map((s: string, i: number) => <li key={i}>{s}</li>)}
                              </ul>
                            </div>
                          )}
                          {(assessment.technical_assessment?.weaknesses ?? []).length > 0 && (
                            <div>
                              <strong>{t('meta_cv.assessment.weaknesses')}</strong>
                              <ul className="list-disc ml-6 text-dark-invirtus/80 text-sm">
                                {(assessment.technical_assessment?.weaknesses ?? []).map((s: string, i: number) => <li key={i}>{s}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                  {assessment.soft_skills_assessment?.strengths && (
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsFeedbackExpanded(!isFeedbackExpanded)}>
                        <div className="flex items-center gap-3 flex-wrap">
                          <h6>{t('meta_cv.assessment.soft_skills_feedback')}</h6>
                          {softScore !== null && softScore !== undefined && (
                            <Tag intent={getScoreIntent(softScore)} variant="subtle" size="lg">
                              {softScore}%
                            </Tag>
                          )}
                          {hasSoftCheatingFlag && <SuspiciousActivityBadge size="lg"/>}
                        </div>
                        <svg className={`w-5 h-5 text-dark-invirtus transform transition-transform ${isFeedbackExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      {isFeedbackExpanded && (
                        <div className="mt-3 space-y-3">
                          {(assessment.soft_skills_assessment?.strengths ?? []).length > 0 && (
                            <div>
                              <strong>{t('meta_cv.assessment.strengths')}</strong>
                              <ul className="list-disc ml-6 text-dark-invirtus/80 text-sm">
                                {(assessment.soft_skills_assessment?.strengths ?? []).map((s: string, i: number) => <li key={i}>{s}</li>)}
                              </ul>
                            </div>
                          )}
                          {(assessment.soft_skills_assessment?.weaknesses ?? []).length > 0 && (
                            <div>
                              <strong>{t('meta_cv.assessment.weaknesses')}</strong>
                              <ul className="list-disc ml-6 text-dark-invirtus/80 text-sm">
                                {(assessment.soft_skills_assessment?.weaknesses ?? []).map((s: string, i: number) => <li key={i}>{s}</li>)}
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            if (hasV3Breakdown) {
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {techBreakdown && Object.keys(techBreakdown).length > 0 && (
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsFeedbackExpanded(!isFeedbackExpanded)}>
                        <div className="flex items-center gap-3 flex-wrap">
                          <h6>{t('meta_cv.assessment.technical_feedback')}</h6>
                          {techScore !== null && techScore !== undefined && (
                            <Tag intent={getScoreIntent(techScore)} variant="subtle" size="lg">
                              {techScore}%
                            </Tag>
                          )}
                          {hasTechnicalCheatingFlag && <SuspiciousActivityBadge size="lg"/>}
                        </div>
                        <svg className={`w-5 h-5 text-dark-invirtus transform transition-transform ${isFeedbackExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      {isFeedbackExpanded && (
                        <div className="mt-3 space-y-3">
                          {Object.entries(techBreakdown).map(([skillName, skillData]: [string, any]) => (
                            <div key={skillName}>
                              <div className="flex items-center gap-2 mb-1">
                                <strong className="text-sm">{skillName}</strong>
                                <Tag intent={getScoreIntent(Math.round((skillData.level ?? 0) * 10))} variant="subtle">
                                  {skillData.level}/10
                                </Tag>
                              </div>
                              {skillData.explanation && (
                                <p className="text-dark-invirtus/80 text-sm ml-1">{skillData.explanation}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                  {softBreakdown && Object.keys(softBreakdown).length > 0 && (
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsFeedbackExpanded(!isFeedbackExpanded)}>
                        <div className="flex items-center gap-3 flex-wrap">
                          <h6>{t('meta_cv.assessment.soft_skills_feedback')}</h6>
                          {softScore !== null && softScore !== undefined && (
                            <Tag intent={getScoreIntent(softScore)} variant="subtle" size="lg">
                              {softScore}%
                            </Tag>
                          )}
                          {hasSoftCheatingFlag && <SuspiciousActivityBadge size="lg"/>}
                        </div>
                        <svg className={`w-5 h-5 text-dark-invirtus transform transition-transform ${isFeedbackExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      {isFeedbackExpanded && (
                        <div className="mt-3 space-y-3">
                          {Object.entries(softBreakdown).map(([skillName, skillData]: [string, any]) => (
                            <div key={skillName}>
                              <div className="flex items-center gap-2 mb-1">
                                <strong className="text-sm">{skillName}</strong>
                                <Tag intent={getScoreIntent(Math.round((skillData.level ?? 0) * 10))} variant="subtle">
                                  {skillData.level}/10
                                </Tag>
                              </div>
                              {skillData.explanation && (
                                <p className="text-dark-invirtus/80 text-sm ml-1">{skillData.explanation}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            if (hasV1Recommendation) {
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {assessment.technical_assessment?.recommendation && (
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsFeedbackExpanded(!isFeedbackExpanded)}>
                        <div className="flex items-center gap-3 flex-wrap">
                          <h6>{t('meta_cv.assessment.technical_feedback')}</h6>
                          {techScore !== null && techScore !== undefined && (
                            <Tag intent={getScoreIntent(techScore)} variant="subtle" size="lg"  >
                              {techScore}%
                            </Tag>
                          )}
                          {hasTechnicalCheatingFlag && <SuspiciousActivityBadge size="lg"/>}
                        </div>
                        <svg className={`w-5 h-5 text-dark-invirtus transform transition-transform ${isFeedbackExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      {isFeedbackExpanded && (
                        <div className="flex mt-3">
                          <div className="mr-3 flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-indigo-400">
                              <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 01-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.513 1.324 1.513 3.518 0 4.842a3.75 3.75 0 01-.837.552c-.676.328-1.028.774-1.028 1.152v.75a.75.75 0 01-1.5 0v-.75c0-1.279 1.06-2.107 1.875-2.502.182-.088.351-.199.503-.331.83-.727.83-1.857 0-2.584zM12 18a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <p className="text-dark-invirtus/90 whitespace-pre-line font-body text-sm">
                            {assessment.technical_assessment.recommendation}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                  {assessment.soft_skills_assessment?.recommendation && (
                    <div className="bg-white p-4 rounded-lg border border-gray-200">
                      <div className="flex items-center justify-between cursor-pointer" onClick={() => setIsFeedbackExpanded(!isFeedbackExpanded)}>
                        <div className="flex items-center gap-3 flex-wrap">
                          <h6>{t('meta_cv.assessment.soft_skills_feedback')}</h6>
                          {softScore !== null && softScore !== undefined && (
                            <Tag intent={getScoreIntent(softScore)} variant="subtle" size="lg">
                              {softScore}%
                            </Tag>
                          )}
                          {hasSoftCheatingFlag && <SuspiciousActivityBadge size="lg"/>}
                        </div>
                        <svg className={`w-5 h-5 text-dark-invirtus transform transition-transform ${isFeedbackExpanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      </div>
                      {isFeedbackExpanded && (
                        <div className="flex mt-3">
                          <div className="mr-3 flex-shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-8 h-8 text-blue-400">
                              <path fillRule="evenodd" d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 01-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.513 1.324 1.513 3.518 0 4.842a3.75 3.75 0 01-.837.552c-.676.328-1.028.774-1.028 1.152v.75a.75.75 0 01-1.5 0v-.75c0-1.279 1.06-2.107 1.875-2.502.182-.088.351-.199.503-.331.83-.727.83-1.857 0-2.584zM12 18a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <p className="text-dark-invirtus/90 whitespace-pre-line font-body text-sm">
                            {assessment.soft_skills_assessment.recommendation}
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }

            return null;
          })()}

          {/* Assessment Scores in 2-Column Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Left Column - Technical Skills */}
            {getSkillsData(assessment, 'Technical Skills').length > 0 && (
              <div className="bg-white px-4 py-6 rounded-lg border border-gray-200">
                <h6 className="mb-4">{t('meta_cv.assessment.technical_skills_scores')}</h6>
                {/* Technical Skills Spider Chart */}
                <div className="flex justify-center items-center py-4">
                  <div className="w-full max-w-sm">
                    <SpiderChart
                      skills={getSkillsData(assessment, 'Technical Skills')}
                      tooltips={getTooltipsWithSuspicion(assessment, 'technical', questionsData, tInferred)}
                      scale={10}
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Right Column - Soft Skills */}
            {getSkillsData(assessment, 'Soft Skills').length > 0 && (
              <div className="bg-white px-4 py-6 rounded-lg border border-gray-200">
                <h6 className="mb-4">{t('meta_cv.assessment.soft_skills_scores')}</h6>
                {/* Soft Skills Spider Chart */}
                <div className="flex justify-center items-center py-4">
                  <div className="w-full max-w-sm">
                    <SpiderChart
                      skills={getSkillsData(assessment, 'Soft Skills')}
                      tooltips={getTooltipsWithSuspicion(assessment, 'soft', questionsData, tInferred)}
                      scale={10}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );
}; 